import * as React from 'react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CrossIcon from "../../../icons/CrossIcon"
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import LockIcon from "@mui/icons-material/Lock";
import { Box, IconButton, LinearProgress, Snackbar, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { clone, each, noop } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { APP_ELEMENT_DIMENSIONS } from '../../constants';
import { activityAtom } from '../../stores/jotai/activities/activities';
import { DEFAULT_SELECTED_WEEK, baseAtom, blockListedBase, calculateCanvasActivity, params_getSlider, setCanvasPeriodValue, setCanvasSliderValue } from '../../stores/jotai/base';
import { canvasAtom, setSelectedHypothesisIDToFilter } from '../../stores/jotai/canvas/canvas';
import { canvasDataAtom } from '../../stores/jotai/canvas/canvasData';
import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { hasPermissionCanvasReOrder, hasPermissionCanvasWrite } from '../../stores/jotai/permissions';
import { capitalizeFirstLetter } from '../../stores/util';
import { useCreateCanvasEntry, useUpdateCanvasEntry } from '../../tanstackQuery/queries/canvas';
import { useCreateLinkSet } from '../../tanstackQuery/queries/canvas/useCreateLinkSet';
import { useReorderBulkEntry } from '../../tanstackQuery/queries/canvas/useReorderBulkEntry';
import { useGetComments } from '../../tanstackQuery/queries/comments';
import { useGetAllFindingLinks } from '../../tanstackQuery/queries/finding-links';
import { findWhereItem } from '../../util';
import { sortEntries } from '../../util/entries';
import isValidCanvasActivity from '../../util/isValidCanvasActivity';
import { getDateOfPeriod } from '../../util/period_utils';
import * as uuid from '../../util/uuid';
import CanvasEntry from "./canvas-entry";
import CategoryExpansionDialog from './CategoryExpansionDialog';
import { CUSTOMER_SEGMENTS_CATEGORY_NAME, HYPOTHESIS_STATES } from './constants';
import CanvasEditingContext from './contexts/CanvasEditingContext';
import CanvasEntryRefContext from "./contexts/CanvasEntryRefContext";
import ExpendViewIcon from "../../../icons/ExpendViewIcon"
interface ErrorInEntryUpdateState {
    error: string;
    message: string;
}

export default function MaterialCanvas(props) {
    const [hovered, setHovered] = React.useState(false);
    const rootNode = React.useRef(null);
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const [heightMap, setHeightMap] = React.useState<any>({});
    const [canvasState, setCanvasState] = useAtom(canvasAtom);
    const [base, setBase] = useAtom(baseAtom);
    const [placeholderState, setPlaceholderState] = React.useState(undefined);
    const { keycloak, initialized } = useKeycloak()
    const [isDragging, setIsDragging] = React.useState<boolean>(false);
    const [hypothesisIDBeingEdited, setHypothesisIDBeingEdited] = React.useState<any>(null);
    const [categoryToExpand, setCategoryToExpand] = React.useState(null);
    const [canvasData,setCanvasData]=useAtom(canvasDataAtom);
    const [activities,setActivities]=useAtom(activityAtom);
    const [isEditCanvas, setIsEditCanvas] = React.useState(false);

    const { data: comments } = useGetComments(
      { accessToken: keycloak?.token },
      { groupID: base?.team?.groupID, sessionID: discoverySession?.sessionID },
      { enabled: !!(base?.team?.groupID && discoverySession?.sessionID) }
    );

    const { data: findingLinksData,isLoading,isSuccess,isError } = useGetAllFindingLinks({ accessToken: keycloak?.token }, { teamID: base?.team?.id }, { enabled: !!(base?.team?.id)})
    const theme = useTheme();
    const client = useQueryClient();
    const { mutateAsync: createLinkSet } = useCreateLinkSet(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          client.invalidateQueries({ queryKey: ["canvas_layout"] });
        }
      }
    );
    const { mutateAsync: createCanvasEntry } = useCreateCanvasEntry(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          client.invalidateQueries({ queryKey: ["canvas_layout"] });
          client.invalidateQueries({ queryKey: ["activities"] });
        }
      }
    );

    const { mutateAsync: updateCanvasEntry } = useUpdateCanvasEntry(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          client.invalidateQueries({ queryKey: ["canvas_layout"] });
          client.invalidateQueries({ queryKey: ["activities"] });
        }
      }
    );
    const { mutateAsync: reorderBulkEntry } = useReorderBulkEntry(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          client.invalidateQueries({ queryKey: ["canvas_layout"] });
        }
      }
    );

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const [currentEntries, setCurrentEntries] = React.useState([]);
    const [hoveredHypothesisId, setHoveredHypothesisId] = React.useState(null);
    const [errorInEntryUpdate, seterrorInEntryUpdate] =
			React.useState<ErrorInEntryUpdateState>(null);
    const [canvasEntryRefCollection, setCanvasEntryRefCollection] = React.useState({});

    const toolbarVisibilityStateSetterRef = React.useRef({
        entryID: null,
        setter: noop
    });

    const { selectedHypothesisIDToFilter } = canvasState

    const isArchiveModeAvailableAndEnabled = useCallback(() => {
        const { isMax } = params_getSlider(base,activities);

        if (canvasData.selectedWeek !== DEFAULT_SELECTED_WEEK) {
            return true;
        }

        return !isMax;
    }, [base.canvasSliderValue, activities, canvasData.selectedWeek]);

    const isInArchiveMode = isArchiveModeAvailableAndEnabled();
    const canEditCanvas = hasPermissionCanvasWrite(base) && !isInArchiveMode;
    const canReOrderCanvas = hasPermissionCanvasReOrder(base) && !isInArchiveMode;

    const canShowAddIconInACategory = (categoryName) =>
			canEditCanvas &&
			!(!!selectedHypothesisIDToFilter && categoryName === CUSTOMER_SEGMENTS_CATEGORY_NAME);

    const canAddEntryInACategory = (categoryName) =>
			canEditCanvas &&
			!(!!selectedHypothesisIDToFilter && categoryName === CUSTOMER_SEGMENTS_CATEGORY_NAME) &&
            !isDragging;

    const unlockCanvas = () => {
        const { canvasSliderMax } = params_getSlider(base,activities);

        setCanvasSliderValue(setBase, canvasSliderMax);
        setCanvasPeriodValue(setCanvasData, 0);
    };

    const propsBasedEntries = useCallback(() => {
        return props?.entries
    }, [props?.entries, comments])

    // initialize our memoization of the category render here so it doesn't change the hook
    // count
    const renderMaterialCategory = useCallback((heightMap: any, category: any, rowHeightSize: any, click?: any, expandedMode: any = false) => {
        return _renderMaterialCategory(heightMap, category, rowHeightSize, click, expandedMode);
    }, [
        blockListedBase(base, ['uppyFiles'])
    ]);

    useEffect(() => {
        
        if (categoryToExpand?.categoryID) {
            const updatedCategoryToExpand = canvasData?.categories?.find(
                (category) => category?.categoryID === categoryToExpand?.categoryID
            );
            setCategoryToExpand(updatedCategoryToExpand);
        }
    }, [props?.entries])

    useEffect(() => {
        const { entries } = props;
        const activityFeed = calculateCanvasActivity(activities);
        let clonedCanvasEntries = entries;

        const {
            canvasSliderValue,
        } = params_getSlider(base,activities);

        // Re assign (in)validated/hypothesis state to what it was at the time
        if (isInArchiveMode) {
            const defaultState = findWhereItem(canvasData?.states, { name: 'Hypothesis' });
            const filteredActivities = activityFeed?.filter(
                (activity) => isValidCanvasActivity(activity)
            );

            // use the period controls
            if (props?.getIsInPeriodOrWeek() === 'period') {
                let currentIndex = 0;

                each(filteredActivities, (activity) => {
                    let { name } = activity;

                    if (entries && entries[activity?.entryID]) {
                        let entry = entries[activity?.entryID];

                        let skip = false;

                        if (entry) {
                            switch (name) {
                                case 'Hypothesis':
                                    if (currentIndex > canvasSliderValue) {
                                        skip = true;
                                        clonedCanvasEntries[entry?.entryID] = undefined;
                                        // don't include it
                                    } else if (entry) {
                                        entry.state = defaultState;
                                        entry.entry = activity?.hypothesis;

                                        clonedCanvasEntries[entry?.entryID] = entry;
                                    }
                                    break;

                                case 'HypothesisStateChange':
                                    if (currentIndex <= canvasSliderValue) {
                                        if (entry) {
                                            entry.state = findWhereItem(canvasData?.states, { name: activity?.hypothesisState });
                                            clonedCanvasEntries[entry?.entryID] = entry;
                                        }
                                    }
                                    else {
                                        // historically reset this entries state to 
                                        entry.state = defaultState;
                                    }
                                    break;

                                case 'EntryContentChange':
                                    // Any activities that happened after our end date we want to use the "to" field for
                                    if (currentIndex <= canvasSliderValue) {
                                        if (entry) {
                                            entry.entry = activity?.hypothesis;
                                            clonedCanvasEntries[entry?.entryID] = entry;
                                        }
                                    }

                                    break;
                            }
                        }
                    }

                    currentIndex += 1;
                });
            }

            if (props.getIsInPeriodOrWeek() === 'week') {
                const currentWeekSelection = props.getCurrentWeek();
                // this should be something other then an incompatible -1 since it's inside this code block
                // { start: moment, end: moment } of what's "in" this selected period
                const currentWeekRange = getDateOfPeriod(props?.cohort, currentWeekSelection.toString());

                each(filteredActivities, activity => {
                    let { name } = activity;

                    if (clonedCanvasEntries && clonedCanvasEntries[activity?.entryID]) {
                        let entry = clonedCanvasEntries[activity?.entryID];
                        const activityDate = moment(activity?.created);
                        const hypothesisCreateDate = moment(entry?.created);

                        switch (name) {
                            case 'Hypothesis':
                                if (
                                    hypothesisCreateDate.isAfter(
                                        currentWeekRange.end
                                    )
                                ) {
                                    delete clonedCanvasEntries[activity?.entryID];
                                } else if (entry) {
                                    clonedCanvasEntries[activity?.entryID].state =
                                        defaultState;
                                    clonedCanvasEntries[activity?.entryID].entry =
                                        activity?.hypothesis;
                                }
                                
                                break;

                            case 'HypothesisStateChange':
                                if (activityDate.isSameOrBefore(currentWeekRange.end)) {
                                    if (entry) {
                                        clonedCanvasEntries[
                                            activity?.entryID
                                        ].state = findWhereItem(canvasData?.states, {
                                            name: activity?.hypothesisState,
                                        });
                                    }
                                }
                                break;

                            case 'EntryContentChange':
                                if (
                                    activityDate.isSameOrBefore(
                                        currentWeekRange.end
                                    )
                                ) {
                                    if (entry)
                                        clonedCanvasEntries[
                                            activity?.entryID
                                        ].entry = activity?.hypothesis;
                                }

                                break;
                        }
                    }
                });
            }
        }
        let useableCanvasEntries: any = {};

        const {
            canvas_filter_showInvalidated,
            canvas_filter_showUnresolved,
            canvas_filter_showValidated
        } = base?.ui;

        each(clonedCanvasEntries, entry => {
            if (entry) {
                let isDeleted = false;

                if (
                    entry &&
                    entry?.state &&
                    (entry.state?.name
                        ? entry?.state?.name === HYPOTHESIS_STATES.DELETED
                        : entry?.state === HYPOTHESIS_STATES.DELETED)
                ) {
                    // Don't include this in our entry list, it's currently considered deleted
                    isDeleted = true;
                }

                if (isDeleted === false) {
                    // Otherwise use it
                    let isValid = true;

                    if (canvasData?.selectedHypothesis !== undefined) {
                        let doesMatchLink = false;

                        each(entry?.hypothesisLinks, link => {
                            if (link?.id === canvasData?.selectedHypothesis) doesMatchLink = true;
                        });

                        if (entry?.entryID === canvasData?.selectedHypothesis) doesMatchLink = true;

                        if (doesMatchLink === false) isValid = false;
                    }

                    if (entry.entry === undefined || entry.entry === null || entry.entry === "" || entry.entry === " ") {
                        isValid = false;
                    }

                    if (isValid) {
                        let foundState = 'unresolved';

                        if (entry && entry?.state) {
                            if (entry?.state?.name === 'Invalidated' || entry?.state === 'Invalidated') {
                                foundState = 'invalidated';
                            }
                            if (entry?.state?.name === 'Validated' || entry?.state === 'Validated') {
                                foundState = 'validated';
                            }
                        }

                        if (entry && entry?.entry !== undefined) {
                            // Finally check our current filters
                            if (foundState === 'unresolved' && canvas_filter_showUnresolved) {
                                useableCanvasEntries[entry?.entryID] = entry;
                            }
                            if (foundState === 'invalidated' && canvas_filter_showInvalidated) {
                                useableCanvasEntries[entry?.entryID] = entry;
                            }
                            if (foundState === 'validated' && canvas_filter_showValidated) {
                                useableCanvasEntries[entry?.entryID] = entry;
                            }
                        }
                    }
                }
            }
        });

        let flattenedList = [];

        each(useableCanvasEntries, entry => { flattenedList.push(entry) });

        flattenedList = sortEntries(flattenedList);

        setCurrentEntries(flattenedList);
    }, [
        propsBasedEntries
    ]);

    // TODO: This can vary depending on the layout
    const getRowCount = () => {
        return 3;
    }

    if (isLoading || !canvasData?.categories || !canvasState?.library) {
        return <LinearProgress className="iw-progress" />;
    }

    const calculateRowHeight = () => {
        let givenFreeSpace = 888;

        const rowCount = getRowCount();

        if (rootNode.current) {
            //@ts-ignore
            givenFreeSpace = rootNode.current.getBoundingClientRect().height;
        }

        return givenFreeSpace / 100;
    }

    // when no state is given assume the default
    const handleEntrySave = ({
        newValue = null,
        category = null,
        entry = null,
        placeholder = false,
        onDone = noop,
        onFail = noop,
    }) => {
        let existingState = entry?.state?.name || "Hypothesis";

        return handleEntrySaveWithState({
            newValue,
            category,
            entry,
            placeholder,
            state: existingState,
            onDone,
            onFail,
        });
    };

    const handleEntrySaveWithState = ({
        newValue = null,
        category = null,
        entry = null,
        placeholder = false,
        state = null,
        onDone = noop,
        onFail = noop,
    }) => {

        let stateID = canvasData?.states[capitalizeFirstLetter(state)]?.id;

        let sid = undefined;

        try {
            // @ts-ignore
            sid = window.__sid;
        } catch (e) {}

        if (placeholder === true) {
            const highestOrder = Math.max(...category?.entries?.map(item => item.sortOrder));
            const orderOfNewEntry = highestOrder + 1;

            const createCanvasEntryPayload = {
                teamID: props?.team?.groupID,
                stateID: stateID,
                data: newValue?.trim(),
                categoryID: category?.categoryID,
                sortOrder: orderOfNewEntry,
                selectedHypothesis: canvasData?.selectedHypothesis,
                sid: sid,
                sessionID: discoverySession?.sessionID
              }

            createCanvasEntry(createCanvasEntryPayload).then(
              (resCreateEntry) => {
                if (selectedHypothesisIDToFilter) {
                  const payload = {
                    groupID: base?.team?.groupID,
                    //@ts-ignore
                    entryID: res?.entryID,
                    targetEntryID: [selectedHypothesisIDToFilter].join(","),
                    sessionID: discoverySession?.sessionID
                  };

                  createLinkSet(payload)
                    .then(() => {
                      setCanvasData((canvasData)=>{
                        const buffer = { ...canvasData };
                        const links = {
                          selectedItems: [selectedHypothesisIDToFilter]
                        };

                        payload.targetEntryID =
                        links.selectedItems[links?.selectedItems?.length - 1];
                        buffer.linkSet.push(payload);
                        return buffer;
                      })
                    })
                    .catch((error) => {
                      onFail(error);
                    });
                }
                onDone();
              }
            );
        } else {
            updateCanvasEntry({
                teamID: props?.team?.groupID,
                entryID: entry?.entryID,
                stateID: stateID,
                data: newValue.trim(),
                categoryID: category?.categoryID,
                sortOrder: entry?.sortOrder,
                selectedHypothesis: canvasData?.selectedHypothesis,
                sid: sid,
                sessionID: discoverySession?.sessionID
            })
              .then((res) => {
                onDone();
              })
              .catch((error) => {
                onFail(error);
              });
        }
    };

    const getCustomerSegmentEntries = () => {
        const {categories}=canvasData;
        const categorieIDs = Object.keys(categories);

        let foundCategory = undefined;

        for (let i = 0; i < categorieIDs?.length; i++) {
            const category = categories[categorieIDs[i]];

            if (category !== undefined) {
                if (category?.name === CUSTOMER_SEGMENTS_CATEGORY_NAME) {
                    foundCategory = category;
                }
            }
        }

        if (foundCategory === undefined) return []

        let cleanedEntries = [];
        
        for (let i = 0; i < foundCategory?.entries?.length; i++) {
            const isDeleted = foundCategory?.entries[i].state?.name
                ? foundCategory?.entries[i]?.state?.name ===
                    HYPOTHESIS_STATES.DELETED
                : foundCategory?.entries[i]?.state === HYPOTHESIS_STATES.DELETED;
                
            if (foundCategory?.entries[i]?.entry !== "" && !isDeleted) {
                cleanedEntries.push(foundCategory?.entries[i]);
            }
            else {

            }
        }

        return cleanedEntries;
    }

    //work here
    const checkIfSourceIsInCategory = (source, category) => {
        let found = false;

        for (let i = 0; i < category?.entries?.length; i++) {
            if (category?.entries[i]?.entryID === source?.entryID) {
                found = true;
                break;
            }
        }

        return found;
    }

    const renderSkinnyCanvasLayoutAs2dArray = (
        skinnyCanvasCategories: {
            categoryLayout: any;
            content: React.ReactElement;
        }[]
    ): { categoryLayout: any; content: React.ReactElement }[][] => {
        const groupedByX: Record<
            string,
            { categoryLayout: any; content: React.ReactElement }[]
        > = {};

        skinnyCanvasCategories.forEach((category) => {
            if (!groupedByX[category?.categoryLayout?.x]) {
                groupedByX[category?.categoryLayout?.x] = [];
            }

            groupedByX[category?.categoryLayout?.x].push(category);
        });

        const canvasLayoutIn2dForm = Object.values(groupedByX).map((group) =>
            group.sort((a, b) => a?.categoryLayout?.y - b?.categoryLayout?.y)
        );

        return canvasLayoutIn2dForm;
    };

    const renderAddCategoryButton = (category) => {
        return canShowAddIconInACategory(category?.name) ? (
            <Tooltip title="Add Hypothesis">
                <IconButton
                    sx={{
                        padding: "0px",
                        marginLeft: "auto",
                    }}
                    onClick={() => {
                        setPlaceholderState(category?.categoryID);
                    }}
                >
                    <AddIcon />
                </IconButton>
            </Tooltip>
        ) : null;
    };

    const renderExpandButton = (category) => {
        return (
            <Tooltip title="Expand Section">
                <IconButton
                    sx={{
                        padding: "5px",
                        marginLeft: canShowAddIconInACategory(category?.name)
                            ? "5px"
                            : "auto",
                        svg: {
                            fontSize: "13px",
                        },
                    }}
                    onClick={() => {
                        setCategoryToExpand(category);
                    }}
                >
                <ExpendViewIcon/>
                </IconButton>
            </Tooltip>
        );
    };

    function handleReorderCanvasEntry(caytegoryEntries: any, sourceIndex: number, destinationIndex: number) {
      const originalOrderedEntries = sortEntries(caytegoryEntries);
      let currentOrderedEntries = clone(originalOrderedEntries);
      const [reorderedEntry] = currentOrderedEntries?.splice(sourceIndex, 1);

      currentOrderedEntries?.splice(destinationIndex, 0, reorderedEntry);
      currentOrderedEntries = currentOrderedEntries?.map((entry, index) => ({
        ...entry,
        sortOrder: index + 1
      }));

      let newOrderSet = [];

      for (let i = 0; i < currentOrderedEntries?.length; i++) {
        newOrderSet.push({
          entryID: currentOrderedEntries[i]?.entryID,
          sortOrder: currentOrderedEntries[i]?.sortOrder
        });
      }

      reorderBulkEntry({
        entries: newOrderSet,
        teamID: base?.team?.groupID,
        sessionID: discoverySession?.sessionID
      }).then((res) =>{});
    }

    const _renderMaterialCategory = (heightMap: any, category: any, rowHeightSize: any, click?: any, expandedMode: any = false) => {
        const {
            entries // this is a string array of ID's
        } = category;

        let entriesJSX = [];

        if (entries === undefined) {
            // TODO: Make this a loading placeholder
            return <div></div>
        }

        const sourceEntries = currentEntries; // for managed version
        let _entries = [];

        // make sure it's in the right category
        for (let i = 0; i < sourceEntries?.length; i++) {
            if (checkIfSourceIsInCategory(sourceEntries[i], category)) {
                _entries.push([sourceEntries[i]?.entryID, sourceEntries[i]]);
            }
        }

        _entries = sortEntries(_entries);


        for (let i = 0; i < _entries?.length; i++) {
            const { linkSet } = canvasData;
            const entryID = _entries[i][0];
            const entryState = !_entries[i][1]?.state?.id ? canvasData?.states[_entries[i][1]?.state || "Hypothesis"] : _entries[i][1]?.state;

            let entry = {
                ..._entries[i][1],
                id: entryID,
                i: i,
                category: category,
                findingLinks: findingLinksData?.findingLinks?.filter(link => link?.entryID === entryID),
                state: entryState,
                linkedEntryIDs: [
                    entryID,
                    ...linkSet
                    ?.filter(
                            (link) =>
                                link?.entryID === entryID || link?.targetEntryID === entryID
                        )
                        .map((link) => {
                            if (link?.entryID === entryID) {
                                return link?.targetEntryID;
                            }
                            
                            return link?.entryID;
                        })
                        .filter(
                            (id) =>
                                props?.entries[id] &&
                                (props?.entries[id]?.state?.name
                                    ? props?.entries[id]?.state?.name !== HYPOTHESIS_STATES.DELETED
                                    : props?.entries[id]?.state !== HYPOTHESIS_STATES.DELETED)
                        ),
                ],
            };
            
            const parentNestedGridRowHeight = 30;
            const objectPaddingAdjustment = 5;

            const entryHeight = heightMap[entryID] ? heightMap[entryID] : (parentNestedGridRowHeight - objectPaddingAdjustment);
            let gridHeight = Math.round(entryHeight / (parentNestedGridRowHeight - objectPaddingAdjustment));

            let highlighted = undefined; // can either be undefined, or a hex color representing a border

            if (canvasState) {
                if (!!selectedHypothesisIDToFilter) {
                    highlighted = "#1976D2";
                } else if (canvasState?.commentEntry) {
                    if (canvasState?.commentEntry?.id === entry?.entryID) {
                        // a convient benefit of the colors coming from this component
                        // is having color schemes be based on the layout or view in the future
                        highlighted = "#1c445b";
                    }
                }
            }

            if (entryHeight !== -1) {
                let foundState = 'unresolved';

                if (entry && entry.state) {
                    if (entry?.state?.name === 'Invalidated') {
                        foundState = 'invalidated';
                    }
                    if (entry?.state?.name === 'Validated') {
                        foundState = 'validated';
                    }
                    if (entry?.state?.name === 'Deleted') {
                        foundState = 'deleted';
                    }
                }

                let entryBackgroundColor = 'white';

                if (foundState === 'validated') {
                    entryBackgroundColor = "#BFE3C0";
                }

                if (foundState === 'invalidated') {
                    entryBackgroundColor = "#F1ADAC";
                }

                const CustomDragHandle = (props) => {
                    return <div style={{ display: 'inline-block', float: 'right', alignItems: 'center', width: '22.5px' }} {...props}><DragIndicatorIcon /></div>
                }

                // each entry internally needs to have a:
                // 

                if (entry.entry !== undefined) {
                    if (foundState !== 'deleted') {
                        const entryWithStateDetails = { ...entry }

                        if (!entry?.state?.id) {
                            entryWithStateDetails.state = canvasData?.states[entry?.state || "Hypothesis"]
                        }

                        entriesJSX.push([
                            (dragHandle) => {
                                return <CanvasEntry
                                    expandedMode={expandedMode}
                                    hideModalsInCanvasSection={hideModalsInCanvasSection}
                                    key={entry?.entryID + "-canvasentry"}
                                    cohort={base?.cohort}
                                    setIsEditCanvas={setIsEditCanvas}
                                    user={props?.user}
                                    comments={comments}
                                    rootNode={rootNode}
                                    entry={entryWithStateDetails}
                                    dragHandle={dragHandle}
                                    csEntries={getCustomerSegmentEntries()}
                                    linkedEntryIDs={entryWithStateDetails?.linkedEntryIDs}
                                    hypothesisLinks={entryWithStateDetails?.hypothesisLinks}
                                    findingLinks={entryWithStateDetails?.findingLinks}
                                    findingLinksData={findingLinksData}
                                    entryBackgroundColor={entryBackgroundColor}
                                    highlighted={highlighted}
                                    isDragging={isDragging}
                                    isInArchiveMode={isInArchiveMode}
                                    toolbarVisibilityStateSetterRef={toolbarVisibilityStateSetterRef}
                                    placeholder={false}
                                    setHoveredHypothesisId={setHoveredHypothesisId}
                                    onClose={() => {
                                        setPlaceholderState(undefined);
                                    }}
                                    onChangeState={(state, setIsSaving, setEntryTitle) => {
                                        setIsSaving(true);

                                        handleEntrySaveWithState({
                                            newValue: entryWithStateDetails?.entry,
                                            category,
                                            entry: entryWithStateDetails,
                                            placeholder: false,
                                            state,
                                            onDone: () => {
                                                setIsSaving(false);
                                            },
                                            onFail: (error) => {
                                                setIsSaving(false);
                                                setEntryTitle(entryWithStateDetails?.entry);
                                                seterrorInEntryUpdate({
                                                    error,
                                                    message: `Could not update the hypothesis titled "${entryWithStateDetails?.entry}"`,
                                                });
                                            },
                                        });
                                    }}
                                    onSave={(newValue, setIsSaving, setEntryTitle) => {
                                        setIsSaving(true);

                                        handleEntrySave({
                                            newValue,
                                            category,
                                            entry: entryWithStateDetails,
                                            placeholder: false,
                                            onDone: () => {
                                                setIsSaving(false);
                                            },
                                            onFail: (error) => {
                                                setIsSaving(false);
                                                setEntryTitle(entryWithStateDetails?.entry);
                                                seterrorInEntryUpdate({
                                                    error,
                                                    message: `Could not update the hypothesis titled "${entryWithStateDetails?.entry}"`,
                                                });
                                            },
                                        });
                                    }}
                                />
                            }, entryID, i]
                        );
                    }
                }
            }
        }

        const hideModalsInCanvasSection = categoryToExpand?.categoryID === category?.categoryID && !expandedMode
        const hasPlaceholder = placeholderState === category?.categoryID && !hideModalsInCanvasSection;

        let placeholderWidget = (dragHandle) => <CanvasEntry
            hideModalsInCanvasSection={hideModalsInCanvasSection}
            expandedMode={expandedMode}
            key={`placeholder-${category?.categoryID}}`}
            commentContextID={props?.commentContextID}
            app={props?.app}
            org={props?.org}
            setIsEditCanvas={setIsEditCanvas}
            cohort={props?.cohort}
            team={props?.team}
            user={props?.user}
            comments={comments}
            placeholder={true}
            dragHandle={dragHandle}
            linkedEntryIDs={selectedHypothesisIDToFilter ? [selectedHypothesisIDToFilter] : []}
            entry={{
                category,
                state: canvasData?.states['Hypothesis']
            }}
            isInArchiveMode={isInArchiveMode}
            toolbarVisibilityStateSetterRef={toolbarVisibilityStateSetterRef}
            onClose={() => {
                setPlaceholderState(undefined);
            }}
            onSave={(newValue, setIsSaving, setEntryTitle) => {
                setIsSaving(true); 
                handleEntrySave({
                    newValue,
                    category,
                    placeholder: true,
                    onDone: () => {
                        setIsSaving(false); 
                        setPlaceholderState(undefined); 
                    },
                    onFail: (error) => {
                        setIsSaving(false); 
                        setEntryTitle("");
                        seterrorInEntryUpdate({
                            error,
                            message: `Could not create hypothesis titled "${newValue}"`,
                        });
                    },
                });
            }}
            findings={props?.findings}
            csEntries={getCustomerSegmentEntries()}
            hypothesisLinks={[]}
            findingLinks={[]}
            entryBackgroundColor={'white'}
            highlighted={true}
            onSize={(size) => {
                
            }}
            setHeightMap={(newHeight, key) => {
                
            }}
        />;

        const handleCategoryRootClick = (target) => {
            let isPlaceholderExpected = false;

            try {
                if (target.classList.contains('event-createPlaceholderOnClick')) {
                    isPlaceholderExpected = true;
                }
            } catch (e) { }

            if (isPlaceholderExpected === true) {
                if (click) {
                    click();
                }
            }
        }

        let droppableEntries = [];

        let canDrag = !isInArchiveMode;

        if (canDrag === true) {
            // make sure they're a TM or instructor

        }

        const CustomDragHandle = (dragProps) => {
            let opacity = undefined;

            try {
                if (dragProps.snapshot.isDragging) {
                    opacity = 0;
                }
            }
            catch (e) {

            }

            return (
                <div
                    className="canvas-entry-draghandle"
                    style={{
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        opacity,
                        visibility: canReOrderCanvas ? "visible" : "hidden",
                    }}
                    {...dragProps}
                >
                    <DragIndicatorIcon sx={{}} />
                </div>
            );
        }

        for (let i = 0; i < entriesJSX?.length; i++) {
            const entryID = entriesJSX[i][1];

            let draggableEntry = <Draggable
                key={"canvas-droppable-section-" + category?.categoryID + "-" + entryID}
                droppableId={"canvas-droppable-section-" + category?.categoryID} draggableId={"canvas-droppable-section-" + category?.categoryID + "-" + entryID} index={entriesJSX[i][2]}
                isDragDisabled={!canReOrderCanvas}
            >
                {(provided, snapshot) => {
                    return (<div
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                            onMouseEnter={() => {}}
                            onMouseLeave={() => {}}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                // height: `${51 + (wikiSection.links.length * 60)}px`,
                                ...provided.draggableProps.style
                            }}
                        >
                            {entriesJSX[i][0](() => {
                                return selectedHypothesisIDToFilter ===
                                  entryID ? (
                                  <Tooltip
                                    title="Click to show all Hypotheses"
                                    placement="top"
                                  >
                                    <span>
                                      <CrossIcon
                                        hovered={hovered}
                                        onMouseEnter={() => setHovered(true)}
                                        onMouseLeave={() => setHovered(false)}
                                        sx={{
                                          margin: "0 4px",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => {
                                          setSelectedHypothesisIDToFilter(
                                            setCanvasState,
                                            ""
                                          );
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <CustomDragHandle
                                    {...provided.dragHandleProps}
                                    snapshot={snapshot}
                                  />
                                );
                            })}
                        </div>
                    );
                }}
            </Draggable>

            droppableEntries.push(draggableEntry);
        }

        return <div
            className='canvas-category-scroll-wrapper event-createPlaceholderOnClick'
            style={{
                padding: "3px 10px",
                display: "block",
                overflowX: "hidden",
                overflowY: "scroll",
                scrollBehavior: "smooth",
                flexGrow: 1,
                backgroundColor: "transparent",
                boxSizing: "border-box",
                // paddingBottom: hasPlaceholder ? "25px" : undefined
            }}
            onScroll={() => {
                toolbarVisibilityStateSetterRef.current.setter(false);
            }}
        >
            <Box
                className="event-createPlaceholderOnClick"
                sx={{
                    display: "flex",
                    height: "100%",
                    minHeight: categoryToExpand ? "500px" : "100%",
                    maxHeight: "77vh",
                    flexDirection: "column",
                    "> div": {
                        flexGrow: Number(!hasPlaceholder),
                    },
                }}>
                <DragDropContext
                    onDragStart={() => {
                        setIsDragging(true);
                    }}
                    onDragEnd={(e) => {
                        // check and save new order of entries
                        try {
                            const targetCategory = e.destination.droppableId.replace("canvas-droppable-section-", "");
                            const caytegoryEntries = currentEntries?.filter(
                                (entry) => entry?.categoryID === targetCategory
                            )

                            handleReorderCanvasEntry(caytegoryEntries, e.source.index, e.destination.index)
                        }
                        catch (e) {

                        }

                        // as onDrop fires the onClick event as well,
                        // we have to disable the onClick function while dragging.
                        // This code block will move the state change to the end of the event queue.
                        // That's why while we will be checking the onclick event,
                        // the state will remain true and we can disable click function then.
                        setTimeout(() => {
                            setIsDragging(false);
                        })

                    }}
                >
                    <Droppable droppableId={"canvas-droppable-section-" + category?.categoryID}>
                        {(provided, snapshot) => {
                            return <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{
                                    ...provided.droppableProps?.style
                                }}
                                onClick={(e) => {
                                    if (canAddEntryInACategory(category?.name) && !hideModalsInCanvasSection) {
                                        handleCategoryRootClick(e.target?.parentElement);
                                    }
                                }}
                            >
                                {droppableEntries}
                                {provided?.placeholder}
                            </div>
                        }}
                    </Droppable>
                </DragDropContext>

                {hasPlaceholder ? <div style={{
                    paddingBottom: "25px"
                }}>{placeholderWidget(undefined)}</div> : undefined}
            </Box>
        </div>
    }


    const calculatedRowHeight = calculateRowHeight();
    const skinnyCanvasCategories = [];
    const topLevelSubGrids = [];

    const getCurrentCanvas = () => {
        let buffer = [];

        for (let i = 0; i < canvasState?.library?.length; i++) {
            if (canvasState?.library[i]?.name === canvasData?.type) {
                buffer = canvasState?.library[i]?.layout?.layout;
            }
        }

        return buffer;
    }

    let isOrientationSkinny = undefined;

    try {
        isOrientationSkinny = base?.ui?.canvas_orientation;
    }
    catch (e) {

    }

    const categorieIDs = Object.keys(canvasData?.categories);
    const currentCanvas = getCurrentCanvas();

    let bb = [];

    const handleDragEnd = (result) => {
        if (!result.destination) return
        const { source, destination } = result
    }


    let maxCategoryCount = 11;
    let incomingLayoutCount = 0;

    try {
        incomingLayoutCount = Object.keys(currentCanvas)?.length;
    } catch(e) {}

    if (incomingLayoutCount > maxCategoryCount) {
        maxCategoryCount = incomingLayoutCount;
    }

    for (let c = 0; c < maxCategoryCount; c++) {
        let category = undefined;
        if (currentCanvas[c] !== undefined && currentCanvas[c].label !== undefined) {
            const layoutName = currentCanvas[c]?.label;
            const orderKey = currentCanvas[c]?.i;

            for (let z = 0; z < categorieIDs?.length; z++) {
                if (parseInt(orderKey) === parseInt(canvasData?.categories[categorieIDs[z]]?.legacySortKey)) {
                    category = canvasData?.categories[categorieIDs[z]];
                }
            }

            if (category === undefined) {
                category = {
                    entries: [],
                    id: uuid.generate(),
                    name: ''
                }
            }

            let renderThisCategory = true;
            let extraStyle = {};

            if (isOrientationSkinny) {
                renderThisCategory = currentCanvas[c]?.favorite === 1;
                extraStyle = {
                    flex: '1'
                }
            }

            let shouldHaveTopBorder = true;

            if (currentCanvas[c].y === 0) {
                shouldHaveTopBorder = false;
            }

            // check and see if it's a hidden category
            if (layoutName === "na") {
                renderThisCategory = false;
            }

            if (renderThisCategory) {
                if (isOrientationSkinny) {
                    skinnyCanvasCategories.push({
                        categoryLayout: currentCanvas[c],
                        content: (
                            <>
                                <strong
                                    style={{
                                        backgroundColor: "transparent",
                                        width: "100%",
                                        display: "flex",
                                        top: "0px",
                                        padding: "10px",
                                        alignItems: "center",
                                    }}
                                    className="text"
                                >
                                    {canvasData?.type==="DThreeCanvas" && layoutName==="Customer Segments" ? "Customer Segments and Stakeholders":layoutName}
                                    {renderAddCategoryButton(category)}
                                    {renderExpandButton(category)}
                                </strong>
                                {renderMaterialCategory(
                                    heightMap,
                                    category,
                                    calculatedRowHeight,
                                    () => {
                                        setPlaceholderState(category?.categoryID);
                                    }
                                )}
                            </>
                        ),
                    });
                }
                else {
                    topLevelSubGrids.push(
                        <div
                            key={orderKey}
                            style={{
                                border: "1px solid #D4D4D4",
                                position: "absolute",
                                display: "inline-flex",
                                flexDirection: "column",
                                width: `${currentCanvas[c].w.toString()}%`,
                                height: `${currentCanvas[c].h.toString()}%`,
                                top: `${currentCanvas[c].y.toString()}%`,
                                left: `${currentCanvas[c].x.toString()}%`,
                                backgroundColor:`${currentCanvas[c].label === "Customer Segments" ? "#F0EBF4" : "inherit"}`,
                                ...extraStyle,
                            }}
                            className={`canvas-hover-category-effect ${
                                isInArchiveMode
                                    ? "canvas-hover-category-effect--locked"
                                    : ""
                            }`}
                        >
                            <strong
                                style={{
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    display: "flex",
                                    top: "0px",
                                    padding: "10px",
                                    alignItems: "center",
                                }}
                                className="text"
                            >
                                {canvasData?.type==="DThreeCanvas" && layoutName==="Customer Segments" ? "Customer Segments and Stakeholders":layoutName}
                                {renderAddCategoryButton(category)}
                                {renderExpandButton(category)}
                            </strong>
                            {renderMaterialCategory(
                                heightMap,
                                category,
                                calculatedRowHeight,
                                () => {
                                    setPlaceholderState(category?.categoryID);
                                }
                            )}
                        </div>
                    );
                }
            }
        }
    }

    let hideDarken = true;

    if (isOrientationSkinny) {
        const canvasLayoutIn2dForm = renderSkinnyCanvasLayoutAs2dArray(
            skinnyCanvasCategories
        );
        const width = 100 / canvasLayoutIn2dForm?.length;

        canvasLayoutIn2dForm.forEach((rows, colIndex) => {
            const height = 100 / rows?.length;
            const left = width * colIndex;

            rows.forEach((row, rowIndex) => {
                const top = height * rowIndex;

                topLevelSubGrids.push(
                    <Box
                        key={row?.categoryLayout?.i}
                        style={{
                            border: "1px solid #D4D4D4",
                            position: "absolute",
                            display: "inline-flex",
                            flexDirection: "column",
                            width: `${width}%`,
                            height: `${height}%`,
                            top: `${top}%`,
                            left: `${left}%`,
                            backgroundColor:`${row?.categoryLayout?.label === "Customer Segments" ? "#F0EBF4" : "inherit"}`
                        }}
                        className={`canvas-hover-category-effect ${
                            isInArchiveMode
                                ? "canvas-hover-category-effect--locked"
                                : ""
                        }`}
                    >
                        {row?.content}
                    </Box>
                );
            });
        });
    }

    let rootCanvasStyle = {
        display: 'block',
        height: `calc(100% - ${APP_ELEMENT_DIMENSIONS.CANVAS_TOOLBAR.HEIGHT})`,
        position: 'relative',
        borderRadius: '4px'
    }

    if (isInArchiveMode) {
        rootCanvasStyle['border'] = '2px solid #1976D2';
    }

    return (
        <CanvasEditingContext.Provider
            value={{
                hypothesisIDBeingEdited,
                setHypothesisIDBeingEdited,
            }}
        >
            <CanvasEntryRefContext.Provider
                value={{
                    canvasEntryRefCollection,
                    setCanvasEntryRefCollection,
                }}
            >
                <div
                    style={rootCanvasStyle}
                    id={"ctr_" + calculatedRowHeight.toString()}
                    ref={rootNode}
                >
                    {isInArchiveMode && (
                        <Tooltip title="Click to unlock canvas">
                            <Stack
                                direction="row"
                                sx={{
                                    alignItems: "center",
                                    position: "absolute",
                                    top: 0,
                                    left: "8px",
                                    padding: "4px 12px",
                                    zIndex: 1,
                                    backgroundColor:
                                    theme?.custom?.mainButtonHighlight,
                                    color: theme?.custom?.white,
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    transform: "translateY(-100%)",
                                    borderRadius: "5px 5px 0 0",
                                    cursor: "pointer",
                                }}
                                onClick={unlockCanvas}
                            >
                                <LockIcon
                                    sx={{
                                        color: theme?.custom?.white,
                                        fontSize: "16px",
                                        marginRight: "4px",
                                    }}
                                />
                                Locked
                            </Stack>
                        </Tooltip>
                    )}
                    {isOrientationSkinny ? (
                        <Box className="iw-canvas">
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="stretch"
                                spacing={0}
                                sx={{ height: "100%" }}
                            >
                                {topLevelSubGrids}
                            </Stack>
                        </Box>
                    ) : (
                        <div className="iw-canvas">
                            <div
                                style={
                                    hideDarken
                                        ? {
                                              display: "none",
                                              position: "absolute",
                                          }
                                        : {}
                                }
                                className="iw-darken"
                            ></div>
                            {topLevelSubGrids}
                        </div>
                    )}
                </div>
                <CategoryExpansionDialog
                    canEditCanvas={canEditCanvas}
                    open={!!categoryToExpand}
                    title={categoryToExpand?.name}
                    isEditCanvas={isEditCanvas}
                    currentCanvas={currentCanvas}
                    onClose={() => setCategoryToExpand(null)}
                    onAdd={() => {
                        setPlaceholderState(
                            categoryToExpand?.categoryID
                        );
                    }}
                >
                    {categoryToExpand
                        ? renderMaterialCategory(
                            heightMap,
                            categoryToExpand,
                            calculatedRowHeight,
                            () => {
                                setPlaceholderState(
                                    categoryToExpand?.categoryID
                                );
                            },
                            true
                        )
                        : null}
                </CategoryExpansionDialog>
                <Snackbar
                    open={!!errorInEntryUpdate}
                    autoHideDuration={5000}
                    onClose={() => {
                        seterrorInEntryUpdate(null);
                    }}
                    message={errorInEntryUpdate?.message}
                />
            </CanvasEntryRefContext.Provider>
        </CanvasEditingContext.Provider>
    );
}
